import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiMapPin, FiClock, FiStar, FiArrowRight, FiInfo, FiCalendar, FiUser, FiHeart, FiCreditCard } from 'react-icons/fi';
import { locations as fallbackLocations, courses as fallbackCourses, therapists as fallbackTherapists } from '../data/therapists';
import { detectNearestLocation } from '../utils/geolocation';
import { useTranslation, useLocalizedData, useLanguage } from '../i18n';
import { fullUrl, OG_IMAGE, hreflangLinks, getLocale, SITE_NAME, buildBusinessSchema, buildFAQSchema } from '../utils/seo';
import TherapistCard from '../components/TherapistCard';
import api, { FILE_BASE_URL } from '../utils/api';
import './HomePage.css';

function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { localize } = useLocalizedData();
  const { lang } = useLanguage();

  const [locations, setLocations] = useState(fallbackLocations);
  const [courses, setCourses] = useState(fallbackCourses);
  const [therapists, setTherapists] = useState(fallbackTherapists);

  const [nearbyLocation, setNearbyLocation] = useState(null);
  const [nearbyDistance, setNearbyDistance] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locRes, courseRes, thRes] = await Promise.all([
          api.get('/locations'),
          api.get('/courses'),
          api.get('/therapists'),
        ]);
        const apiLocations = locRes.data.map(l => ({
          id: l.id,
          name: { ko: l.nameKo, en: l.nameEn, zh: l.nameZh, ja: l.nameJa, th: l.nameTh, fr: l.nameFr, de: l.nameDe, es: l.nameEs },
          description: { ko: l.descKo, en: l.descEn, zh: l.descZh, ja: l.descJa, th: l.descTh, fr: l.descFr, de: l.descDe, es: l.descEs },
          image: l.image, lat: l.lat, lng: l.lng,
        }));
        const apiCourses = courseRes.data.map(c => ({
          id: c.id,
          name: { ko: c.nameKo, en: c.nameEn, zh: c.nameZh, ja: c.nameJa, th: c.nameTh, fr: c.nameFr, de: c.nameDe, es: c.nameEs },
          description: { ko: c.descKo, en: c.descEn, zh: c.descZh, ja: c.descJa, th: c.descTh, fr: c.descFr, de: c.descDe, es: c.descEs },
          icon: c.icon,
          options: (c.options || []).map(o => ({ duration: o.duration, price: o.price })),
        }));
        const apiTherapists = thRes.data.map(t => ({
          id: t.id, name: t.name,
          nameKo: { ko: t.nameKo, en: t.nameEn, zh: t.nameZh, ja: t.nameJa, th: t.nameTh, fr: t.nameFr, de: t.nameDe, es: t.nameEs },
          bio: { ko: t.bioKo, en: t.bioEn, zh: t.bioZh, ja: t.bioJa, th: t.bioTh, fr: t.bioFr, de: t.bioDe, es: t.bioEs },
          age: t.age, experience: t.experience, location: t.locationId,
          specialties: t.specialtyIds || [], rating: t.rating, photo: t.photo,
          height: t.height, weight: t.weight,
          features: t.features ? { ko: t.featuresKo, en: t.featuresEn, zh: t.featuresZh, ja: t.featuresJa, th: t.featuresTh, fr: t.featuresFr, de: t.featuresDe, es: t.featuresEs } : null,
        }));
        setLocations(apiLocations);
        setCourses(apiCourses);
        setTherapists(apiTherapists);
      } catch (err) {
        console.log('API unavailable, using fallback data');
      }
    };
    fetchData();
  }, []);

  const handleDetectNearby = () => {
    detectNearestLocation()
      .then((result) => {
        setNearbyLocation(result.location);
        setNearbyDistance(Math.round(result.distance));
      })
      .catch(() => {});
  };

  useEffect(() => {
    handleDetectNearby();
  }, []);

  const nearbyTherapists = nearbyLocation
    ? therapists.filter((th) => th.location === nearbyLocation.id).slice(0, 3)
    : [];

  const featuredTherapists = therapists.filter((t) => t.rating >= 4.9).slice(0, 3);

  return (
    <div className="home-page">
      <Helmet>
        <html lang={lang} />
        <title>{t('seo_home_title')}</title>
        <meta name="description" content={t('seo_home_desc')} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={t('seo_home_title')} />
        <meta property="og:description" content={t('seo_home_desc')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={fullUrl('/')} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content={getLocale(lang)} />
        <link rel="canonical" href={fullUrl('/')} />
        {hreflangLinks('/').map(({ rel, hreflang, href }) => (
          <link key={hreflang} rel={rel} hrefLang={hreflang} href={href} />
        ))}
        <script type="application/ld+json">{JSON.stringify(buildBusinessSchema(t('seo_home_desc')))}</script>
        <script type="application/ld+json">{JSON.stringify(buildFAQSchema([
          { question: t('faq_q1'), answer: t('faq_a1') },
          { question: t('faq_q2'), answer: t('faq_a2') },
          { question: t('faq_q3'), answer: t('faq_a3') },
        ]))}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="hero-badge">{t('hero_badge')}</div>
          <h1 className="hero-title">
            {t('hero_title').split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </h1>
          <p className="hero-subtitle">
            {t('hero_subtitle')}
          </p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={() => navigate('/booking')}>
              {t('hero_book_now')} <FiArrowRight />
            </button>
            <button className="btn-secondary" onClick={() => navigate('/therapists')}>
              {t('hero_view_therapists')}
            </button>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">17+</span>
              <span className="stat-label">{t('stat_therapists')}</span>
            </div>
            <div className="stat">
              <span className="stat-number">5</span>
              <span className="stat-label">{t('stat_locations')}</span>
            </div>
            <div className="stat">
              <span className="stat-number">4.8</span>
              <span className="stat-label">{t('stat_rating')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features">
        <div className="container">
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon"><FiMapPin /></div>
              <h3>{t('feature_gps_title')}</h3>
              <p>{t('feature_gps_desc')}</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><FiClock /></div>
              <h3>{t('feature_booking_title')}</h3>
              <p>{t('feature_booking_desc')}</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><FiStar /></div>
              <h3>{t('feature_verified_title')}</h3>
              <p>{t('feature_verified_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby Therapists (GPS) — 권한 허용 후 성공 시에만 표시 */}
      {nearbyLocation && nearbyTherapists.length > 0 && (
        <section className="section nearby-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title"><FiMapPin className="nearby-title-icon" /> {t('nearby_title')}</h2>
              <p className="section-subtitle">{t('nearby_subtitle')}</p>
            </div>

            <div className="nearby-location-badge">
              <FiMapPin />
              <span>{localize(nearbyLocation.name)}</span>
              <span className="nearby-distance">{t('nearby_distance', { distance: nearbyDistance })}</span>
            </div>
            <div className="nearby-grid">
              {nearbyTherapists.map((th) => (
                <div key={th.id} className="tl-card-wrapper" onClick={() => navigate('/booking', { state: { locationId: nearbyLocation.id, therapistId: th.id } })}>
                  <TherapistCard therapist={th} showDetails onClick={() => {}} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Guide */}
      <section className="section guide-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('guide_title')}</h2>
            <p className="section-subtitle">{t('guide_subtitle')}</p>
          </div>
          <div className="guide-grid">
            <div className="guide-item">
              <div className="guide-icon"><FiInfo /></div>
              <h3>{t('guide_before_title')}</h3>
              <p>{t('guide_before_text')}</p>
            </div>
            <div className="guide-item">
              <div className="guide-icon"><FiCalendar /></div>
              <h3>{t('guide_booking_title')}</h3>
              <p>{t('guide_booking_text')}</p>
            </div>
            <div className="guide-item">
              <div className="guide-icon"><FiUser /></div>
              <h3>{t('guide_session_title')}</h3>
              <p>{t('guide_session_text')}</p>
            </div>
            <div className="guide-item">
              <div className="guide-icon"><FiHeart /></div>
              <h3>{t('guide_after_title')}</h3>
              <p>{t('guide_after_text')}</p>
            </div>
            <div className="guide-item">
              <div className="guide-icon"><FiCreditCard /></div>
              <h3>{t('guide_payment_title')}</h3>
              <p>{t('guide_payment_text')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="section locations-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('section_locations_title')}</h2>
            <p className="section-subtitle">{t('section_locations_subtitle')}</p>
          </div>
          <div className="locations-grid">
            {locations.map((loc) => (
              <div
                key={loc.id}
                className="home-location-card"
                onClick={() => navigate('/booking', { state: { locationId: loc.id } })}
              >
                <div className="home-loc-emoji">{loc.image}</div>
                <h3 className="home-loc-name">
                  <img className="home-loc-flag" src="https://flagcdn.com/w40/th.png" alt="Thailand" loading="lazy" />
                  {localize(loc.name)}
                </h3>
                <p className="home-loc-desc">{localize(loc.description)}</p>
                <span className="home-loc-link">
                  {t('loc_book')} <FiArrowRight />
                </span>
              </div>
            ))}
          </div>
          <div className="location-notices">
            <p className="location-notice"><FiInfo className="notice-icon" /> {t('notice_punctual')}</p>
            <p className="location-notice"><FiInfo className="notice-icon" /> {t('notice_no_sexual')}</p>
          </div>
        </div>
      </section>

      {/* Courses */}
      <section className="section courses-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('section_courses_title')}</h2>
            <p className="section-subtitle">{t('section_courses_subtitle')}</p>
          </div>
          <div className="courses-grid">
            {courses.map((course) => (
              <div key={course.id} className="home-course-card">
                <span className="home-course-icon">{course.icon}</span>
                <h3>{localize(course.name)}</h3>
                <p className="home-course-desc">{localize(course.description)}</p>
                <div className="home-course-price">
                  {course.options[0].price.toLocaleString()}฿ ~
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Therapists */}
      <section className="section therapists-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('section_therapists_title')}</h2>
            <p className="section-subtitle">{t('section_therapists_subtitle')}</p>
          </div>
          <div className="featured-grid">
            {featuredTherapists.map((th) => {
              const loc = locations.find((l) => l.id === th.location);
              return (
                <div key={th.id} className="featured-card">
                  {th.photo ? (
                    <img className="featured-avatar-img" src={`${FILE_BASE_URL}/${th.photo}`} alt={th.name} loading="lazy" />
                  ) : (
                    <div className="featured-avatar">
                      {th.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <h3>{localize(th.nameKo)} <span>({th.name})</span></h3>
                  <div className="featured-rating">
                    <FiStar className="star-icon" /> {th.rating}
                  </div>
                  <p className="featured-location">
                    {localize(loc?.name)} | {t('experience_years', { years: th.experience })}
                  </p>
                  <p className="featured-bio">{localize(th.bio)}</p>
                </div>
              );
            })}
          </div>
          <div className="section-cta">
            <button className="btn-secondary" onClick={() => navigate('/therapists')}>
              {t('view_all_therapists')} <FiArrowRight />
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>{t('cta_title')}</h2>
          <p>{t('cta_subtitle')}</p>
          <button className="btn-primary btn-lg" onClick={() => navigate('/booking')}>
            {t('cta_book')} <FiArrowRight />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>{t('footer_copyright')}</p>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
